import { BadgeCheck } from "lucide-react";
import type { CampaignContact, EmailVerification } from "@/api/email";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";
import { Hint, ScopeBadge } from "@/components/email/EmailBadges";
import { VERIFICATION_HINT, verificationLabel } from "@/lib/email";
import { formatDate } from "@/lib/formatters";
import { cn } from "@/lib/utils";

/** What Prism has on record about an address — never more than that. */
export function VerificationMark({
  verification,
  className,
}: {
  verification: EmailVerification | null | undefined;
  className?: string;
}) {
  const verified = verification?.state === "VERIFIED";
  const hint = verified
    ? `${VERIFICATION_HINT.VERIFIED}${verification?.verified_at ? ` Checked ${formatDate(verification.verified_at, "d MMM yyyy")}.` : ""}`
    : VERIFICATION_HINT.NONE;
  return (
    <Hint content={hint}>
      <span
        className={cn(
          "inline-flex items-center gap-1 whitespace-nowrap text-[11px]",
          verified ? "font-medium text-emerald-700" : "text-slate-400",
          className,
        )}
      >
        {verified && <BadgeCheck className="h-3 w-3" />}
        {verificationLabel(verification)}
      </span>
    </Hint>
  );
}

/** The lead's address as the viewer may see it, and what is on record about it. */
export function EmailCell({ row, showVerification = true }: { row: CampaignContact; showVerification?: boolean }) {
  return (
    <div className="min-w-0">
      {isHidden(row, "email") ? (
        <HiddenDetail label="Email" className="text-xs" />
      ) : row.email ? (
        <span className="block truncate text-slate-700">{row.email}</span>
      ) : (
        <span className="text-xs font-medium text-rose-600">No email</span>
      )}
      {showVerification && row.email !== null && !isHidden(row, "email") && (
        <VerificationMark verification={row.email_verification} />
      )}
    </div>
  );
}

export function LeadCell({ row }: { row: Pick<CampaignContact, "contact_name" | "contact_title"> }) {
  return (
    <div className="min-w-0">
      <p className="truncate font-medium text-slate-900">{row.contact_name}</p>
      {row.contact_title && <p className="truncate text-xs text-slate-500">{row.contact_title}</p>}
    </div>
  );
}

export function CompanyCell({
  row,
  scope = false,
}: {
  row: Pick<CampaignContact, "company_name" | "scope" | "business_status_label">;
  scope?: boolean;
}) {
  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1.5">
        {scope && <ScopeBadge scope={row.scope} />}
        <span className="truncate text-slate-700">{row.company_name ?? "—"}</span>
      </div>
      {row.business_status_label && (
        <p className="truncate text-[11px] text-slate-400">{row.business_status_label}</p>
      )}
    </div>
  );
}
